// ============================================
// COMPANY SETTINGS (CLIENT-SIDE)
// ============================================
// Company-wide settings, for the consulting firm (client page › Settings) and
// the company's own admin (avatar menu › Company admin):
// - Company profile (CompanyProfileCard) and apps and licenses (CompanyAppsCard),
//   restored from the old CompanyProfileEditor (RESTORE_PLAN R3.3)
// - AI switch (company-wide): the server refuses every AI call for the company while it is off,
//   with an optional reason and who turned it off and when
// - AI mode: everyone, or consultants only (the company's own users get no AI; enforced in openai.ts)
// - AI call log: every AI request made for the company
// - Email notifications toggle
// - In-app notifications toggle

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Alert, AlertDescription } from './ui/alert';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Textarea } from './ui/textarea';
import { Settings, Save, Loader2, AlertTriangle, Sparkles, Mail, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { bearerAuthHeader } from '../utils/apiHeaders';
import { AICallLog } from './ai/AICallLog';
import { CompanyProfileCard } from './CompanyProfileCard';
import { CompanyAppsCard } from './CompanyAppsCard';
import type { AiMode } from '../utils/companyProfile';

interface CompanySettingsProps {
  accessToken: string;
  companyId: string;
  companyName: string;
  currentUser: any;
}

interface CompanyPreferences {
  aiEnabled: boolean;
  aiMode: AiMode;
  aiDisabledReason: string;
  emailNotifications: boolean;
  inAppNotifications: boolean;
}

/** Who turned AI off and when (set by the server when the switch goes off). */
interface AiDisabledInfo {
  at: string | null;
  byName: string | null;
}

export function CompanySettings({
  accessToken,
  companyId,
  companyName,
  currentUser,
}: CompanySettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Form state
  const [aiEnabled, setAiEnabled] = useState(true); // Default ON
  const [aiMode, setAiMode] = useState<AiMode>('full');
  const [aiDisabledReason, setAiDisabledReason] = useState('');
  const [aiDisabled, setAiDisabled] = useState<AiDisabledInfo>({ at: null, byName: null });
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);

  // Track initial values to detect changes
  const [initialPreferences, setInitialPreferences] = useState<CompanyPreferences>({
    aiEnabled: true,
    aiMode: 'full',
    aiDisabledReason: '',
    emailNotifications: true,
    inAppNotifications: true,
  });

  useEffect(() => {
    loadCompanyPreferences();
  }, [companyId]);

  async function loadCompanyPreferences() {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/preferences`,
        { headers: { ...bearerAuthHeader() } }
      );

      if (response.ok) {
        const data = await response.json();
        const prefs = data.preferences || {};
        
        // Set values with defaults
        const aiEnabledValue = prefs.aiEnabled !== undefined ? prefs.aiEnabled : true;
        const emailValue = prefs.emailNotifications !== undefined ? prefs.emailNotifications : true;
        const inAppValue = prefs.inAppNotifications !== undefined ? prefs.inAppNotifications : true;
        
        const modeValue: AiMode = prefs.aiMode === 'consultant_only' ? 'consultant_only' : 'full';
        const reasonValue = typeof prefs.aiDisabledReason === 'string' ? prefs.aiDisabledReason : '';
        setAiEnabled(aiEnabledValue);
        setAiMode(modeValue);
        setAiDisabledReason(reasonValue);
        setAiDisabled({ at: prefs.aiDisabledAt ?? null, byName: prefs.aiDisabledByName ?? null });
        setEmailNotifications(emailValue);
        setInAppNotifications(inAppValue);
        
        setInitialPreferences({
          aiEnabled: aiEnabledValue,
          aiMode: modeValue,
          aiDisabledReason: reasonValue,
          emailNotifications: emailValue,
          inAppNotifications: inAppValue,
        });
        setHasChanges(false);
      } else {
        console.error('Failed to load company preferences');
      }
    } catch (error) {
      console.error('Error loading company preferences:', error);
    } finally {
      setLoading(false);
    }
  }

  async function saveSettings() {
    try {
      setSaving(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/preferences`,
        {
          method: 'PATCH',
          headers: { ...bearerAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            aiEnabled,
            aiMode,
            // The reason belongs to AI being off; turning it back on clears it on the server
            ...(aiEnabled ? {} : { aiDisabledReason }),
            emailNotifications,
            inAppNotifications,
          }),
        }
      );

      if (response.ok) {
        const saved = await response.json().catch(() => ({}));
        toast.success('Company settings saved successfully');
        setHasChanges(false);
        const savedReason = typeof saved?.preferences?.aiDisabledReason === 'string' ? saved.preferences.aiDisabledReason : '';
        setAiDisabledReason(aiEnabled ? '' : savedReason);
        setAiDisabled({ at: saved?.preferences?.aiDisabledAt ?? null, byName: saved?.preferences?.aiDisabledByName ?? null });
        
        // Update initial values
        setInitialPreferences({
          aiEnabled,
          aiMode,
          aiDisabledReason: aiEnabled ? '' : savedReason,
          emailNotifications,
          inAppNotifications,
        });
      } else {
        const errorText = await response.text();
        console.error('Failed to save settings:', errorText);
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  }

  function resetChanges() {
    setAiEnabled(initialPreferences.aiEnabled);
    setAiMode(initialPreferences.aiMode);
    setAiDisabledReason(initialPreferences.aiDisabledReason);
    setEmailNotifications(initialPreferences.emailNotifications);
    setInAppNotifications(initialPreferences.inAppNotifications);
    setHasChanges(false);
  }

  // Track changes
  useEffect(() => {
    const changed = 
      aiEnabled !== initialPreferences.aiEnabled ||
      aiMode !== initialPreferences.aiMode ||
      (!aiEnabled && aiDisabledReason !== initialPreferences.aiDisabledReason) ||
      emailNotifications !== initialPreferences.emailNotifications ||
      inAppNotifications !== initialPreferences.inAppNotifications;
    setHasChanges(changed);
  }, [aiEnabled, aiMode, aiDisabledReason, emailNotifications, inAppNotifications, initialPreferences]);

  // The firm's side of the company (the client page) sees the pointer to the firm's license portfolio
  const firmSide = !currentUser?.companyId || currentUser.companyId !== companyId;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <CompanyProfileCard companyId={companyId} />
      <CompanyAppsCard companyId={companyId} firmSide={firmSide} />
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="size-5 text-blue-500" />
                Company Settings
              </CardTitle>
              <CardDescription>
                Manage company-wide preferences for {companyName}
              </CardDescription>
            </div>
            {hasChanges && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetChanges} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={saveSettings} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="size-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {hasChanges && (
            <Alert>
              <AlertTriangle className="size-4" />
              <AlertDescription>
                You have unsaved changes. Click "Save Changes" to apply your updates.
              </AlertDescription>
            </Alert>
          )}

          {/* AI Features */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-start gap-3 flex-1">
                <Sparkles className="size-5 text-purple-500 mt-0.5" />
                <div>
                  <Label htmlFor="ai-enabled" className="text-base cursor-pointer">
                    AI Assistance
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    AI-powered discovery tools and suggestions for your company and your consultants. While this is off,
                    PathKit sends none of your company's data to AI: every AI feature is turned off for this company.
                  </p>
                </div>
              </div>
              <Switch
                id="ai-enabled"
                checked={aiEnabled}
                onCheckedChange={setAiEnabled}
              />
            </div>

            {aiEnabled ? (
              <div className="space-y-3 pl-4">
                <Label>Who can use AI for this company</Label>
                <RadioGroup value={aiMode} onValueChange={(v) => setAiMode(v as AiMode)} aria-label="Who can use AI for this company">
                  <div className="flex items-start gap-3 rounded-md border p-3">
                    <RadioGroupItem value="full" id="ai-mode-full" />
                    <div>
                      <Label htmlFor="ai-mode-full" className="cursor-pointer">Everyone</Label>
                      <p className="text-muted-foreground">The consulting team and the company's own people.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-md border p-3">
                    <RadioGroupItem value="consultant_only" id="ai-mode-consultant" />
                    <div>
                      <Label htmlFor="ai-mode-consultant" className="cursor-pointer">Consultants only</Label>
                      <p className="text-muted-foreground">
                        Only the consulting team uses AI for this company; the company's own people get no AI features.
                        For clients with strict privacy policies.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            ) : (
              <div className="space-y-2 pl-4">
                <Label htmlFor="ai-disabled-reason">Reason for turning AI off (optional)</Label>
                <Textarea
                  id="ai-disabled-reason"
                  rows={2}
                  value={aiDisabledReason}
                  onChange={(e) => setAiDisabledReason(e.target.value)}
                  placeholder="e.g. client privacy policy, regulatory requirements, client request"
                />
                {initialPreferences.aiEnabled === false && aiDisabled.at && (
                  <p className="text-muted-foreground" data-testid="ai-disabled-info">
                    Turned off on {new Date(aiDisabled.at).toLocaleDateString()}{aiDisabled.byName ? ` by ${aiDisabled.byName}` : ''}.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-base font-medium">Notification Preferences</h3>
          
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-start gap-3 flex-1">
                  <Mail className="size-5 text-blue-500 mt-0.5" />
                  <div>
                    <Label htmlFor="email-notifications" className="text-base cursor-pointer">
                      Email Notifications
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Receive email updates for engagement progress and important changes
                    </p>
                  </div>
                </div>
                <Switch
                  id="email-notifications"
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-start gap-3 flex-1">
                  <Bell className="size-5 text-green-500 mt-0.5" />
                  <div>
                    <Label htmlFor="in-app-notifications" className="text-base cursor-pointer">
                      In-App Notifications
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Show notifications within the application for real-time updates
                    </p>
                  </div>
                </div>
                <Switch
                  id="in-app-notifications"
                  checked={inAppNotifications}
                  onCheckedChange={setInAppNotifications}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <AICallLog scope="company" companyId={companyId} />
    </div>
  );
}
